import { createContext, Dispatch, FC, ReactNode, SetStateAction, useState, useEffect } from 'react';
import ToastClose from '@assets/svg/ToastClose.svg?react';
import ToastError from '@assets/svg/ToastError.svg?react';
import ToastSuccess from '@assets/svg/ToastSuccess.svg?react';
import {
  ToastContainer,
  ToastContent,
  ToastMessage,
  ToastCloseButton,
  ToastIcon,
  SnackbarContainer,
  SnackbarWrapper,
  WarningAlert,
  WarningCloseButton,
  IconSvg
} from './toast.styles';


interface ContextProps {
  showError: Dispatch<SetStateAction<string | undefined>>;
  showSuccess: Dispatch<SetStateAction<string | undefined>>;
  showWarning: Dispatch<SetStateAction<string | undefined>>;
}
const ToastMessageContext = createContext<ContextProps>({
  showError: (string) => { },
  showSuccess: (string) => { },
  showWarning: (string) => { },
});


const ToastMessageProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [successMessage, setSuccessMessage] = useState<string | undefined>();
  const [warningMessage, setWarningMessage] = useState<string | undefined>();
  
  const lang = localStorage.getItem('i18nextLng') || 'ar';
  const isAr = lang === 'ar';

  const getMinWidth = () => {
    const currentMessage = errorMessage || successMessage || '';
    return currentMessage.length > 60 ? '700px' : '485px';
  };

  const handleClose = () => {
    setSuccessMessage(undefined);
    setErrorMessage(undefined);
  };

  const handleWarningClose = () => {
    setWarningMessage(undefined);
  };

  // Auto-hide effect for success and error messages
  useEffect(() => {
    if (errorMessage || successMessage) {
      const timer = setTimeout(() => {
        handleClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage, successMessage]);

  // Auto-hide effect for warning messages
  useEffect(() => {
    if (warningMessage) {
      const timer = setTimeout(() => {
        handleWarningClose();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [warningMessage]);
  return (
    <ToastMessageContext.Provider
      value={{
        showError: setErrorMessage,
        showSuccess: setSuccessMessage,
        showWarning: setWarningMessage,
      }}
    >
      {children}
      
      {/* Error and Success Messages */}
      {(errorMessage || successMessage) && (
        <SnackbarContainer>
          <SnackbarWrapper $isVisible={Boolean(errorMessage || successMessage)}>
            <ToastContainer $isAr={isAr} $minWidth={getMinWidth()}>
              <ToastContent $isAr={isAr}>
                <ToastIcon>
                  <IconSvg>
                    {successMessage ? 
                      <ToastSuccess /> : 
                      <ToastError />
                    }
                  </IconSvg>
                </ToastIcon>
                <ToastMessage>
                  {errorMessage || successMessage}
                </ToastMessage>
              </ToastContent>
              
              <ToastCloseButton onClick={handleClose}>
                <ToastClose />
              </ToastCloseButton>
            </ToastContainer>
          </SnackbarWrapper>
        </SnackbarContainer>
      )}
      
      {/* Warning Messages */}
      {warningMessage && (
        <SnackbarContainer>
          <SnackbarWrapper $isVisible={Boolean(warningMessage)}>
            <WarningAlert>
              <span>{warningMessage}</span>
              <WarningCloseButton onClick={handleWarningClose}>
                ×
              </WarningCloseButton>
            </WarningAlert>
          </SnackbarWrapper>
        </SnackbarContainer>
      )}
    </ToastMessageContext.Provider>
  );
};

export { ToastMessageContext, ToastMessageProvider };
